import React, { useMemo } from 'react';
import { Table, Typography } from 'antd';
import { useSelector } from 'react-redux';
import { format, parse } from 'date-fns';

const { Text } = Typography;

const parseNum = (v) => {
  if (v === null || v === undefined) return 0;
  const n = Number(String(v).replace(/,/g, '').trim());
  return Number.isFinite(n) ? n : 0;
};

// Retained as a fallback for the very first render, before `systemDate` has
// loaded from Redux.
const getCurrentFYStart = () => {
  const today = new Date();
  return today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
};

// NEW: FY start now comes from the admin-configured System Start Date
// (Redux `otherProjects.systemDate`, format DD-MM-YYYY) instead of today's date.
const parseSystemFYStart = (startDate) => {
  if (!startDate) return null;

  const [day, month, year] = startDate.split("-").map(Number);

  if (!day || !month || !year) return null;

  return month >= 4 ? year : year - 1; // Apr-Mar FY cycle
};

// OLD: "upto" was rendered straight off the API's static field, and the
// title was anchored to this project's own UPTO_DATE.
// NEW: Upto = the API's baseline "upto" plus every FY-year key ("YYYY-YYYY")
// that falls strictly before the current (system-date-derived) FY - same
// "dynamic upto" rule used across the other reports/tables. Unlike those,
// this component's rows carry only plain year keys (no "_formatted" variant),
// so those are summed directly.
const getDynamicUpto = (row = {}, fyStart, baseField) => {
  let sum = parseNum(row[baseField]);

  Object.keys(row).forEach((key) => {
    if (!/^\d{4}-\d{4}$/.test(key)) return;

    const fyStartYear = Number(key.split("-")[0]);
    if (fyStartYear < fyStart) {
      sum += parseNum(row[key]);
    }
  });

  return sum.toFixed(2);
};

const ProjectSummaryTable = ({ version = "C" }) => {
  const { projectDates, projectSummary, projectSummaryVersioned } = useSelector((state) => state.budget);
  const { systemDate } = useSelector((state) => state.otherProjects);

  // OLD: const CURRENT_FY_START = getCurrentFYStart(); (fixed once at import time)
  // NEW: derive it from the admin-configured System Start Date, falling back
  // to today's date until systemDate has loaded.
  const CURRENT_FY_START = useMemo(
    () => parseSystemFYStart(systemDate?.STARTDATE) ?? getCurrentFYStart(),
    [systemDate]
  );

  // OLD: title was anchored to this project's own UPTO_DATE.
  // const formattedUptoDate = projectDates?.UPTO_DATE
  //   ? format(parse(projectDates.UPTO_DATE, 'dd-MM-yyyy', new Date()), "MMM''yy")
  //   : '';

  // OLD: const rawData = version === "C" ? projectSummary : projectSummaryVersioned;
  // NEW: override "upto" on every row with the dynamic value before it's used
  // for both column generation and the table's dataSource.
  const data = useMemo(() => {
    const rawData = version === "C" ? projectSummary : projectSummaryVersioned;

    if (!rawData) return rawData;

    return rawData.map((row) => ({
      ...row,
      upto: getDynamicUpto(row, CURRENT_FY_START, "upto"),
    }));
  }, [projectSummary, projectSummaryVersioned, version, CURRENT_FY_START]);

  const generateColumns = () => {
    if (!data || data.length === 0) return [];

    const keys = Object.keys(data[0]);

    // Fixed starting keys
    const fixedStart = ["description", "upto"];
    const totalKey = "Total";

    // Filter out fixed keys, and any FY year column that's already folded
    // into "upto" above (fyStartYear < CURRENT_FY_START) - same visibility
    // rule as visibleYearLabels in the other reports.
    const dynamicKeys = keys.filter((key) => {
      if ([...fixedStart, totalKey].includes(key)) return false;

      if (/^\d{4}-\d{4}$/.test(key)) {
        return Number(key.split("-")[0]) >= CURRENT_FY_START;
      }

      return true;
    });

    // Final order: description → upto → dynamic years → total
    const orderedKeys = [...fixedStart, ...dynamicKeys.sort(), totalKey];

    return orderedKeys.map((key) => ({
      title:
        key === "description"
          ? "Description"
          : key === "upto"
            ? `Upto Mar'${String(CURRENT_FY_START).slice(-2)}`
            : key === "total"
              ? "Total"
              : key,
      dataIndex: key,
      key,
      align: "center",
      render: (text, record) => {
        if (key === "description") {
          return <Text>{text?.replace(/_/g, " ")}</Text>;
        }

        // Extract expected year
        const expectedDate = projectDates?.EXPECTED_DATE;
        let expectedYear = null;

        if (expectedDate) {
          const parsed = parse(expectedDate, "dd-MM-yyyy", new Date());
          expectedYear = parsed.getFullYear();
        }

        // Check if column is a year column like "2026-2027"
        const isYearColumn = /^\d{4}-\d{4}$/.test(key);

        let isBeyondExpected = false;

        if (isYearColumn && expectedYear) {
          const columnStartYear = parseInt(key.split("-")[0]);
          isBeyondExpected = columnStartYear > expectedYear;
        }

        return (
          <div
            className={`text-right ${isBeyondExpected ? "text-blue-500 font-medium" : ""
              }`}
          >
            {text}
          </div>
        );
      }
    }));
  };


  return (
    <Table
      columns={generateColumns()}
      dataSource={data}
      pagination={false}
      className={` custom-ant-table`}
      bordered
    />
  );
};

export default ProjectSummaryTable;
